import { GTMEvent } from '@corratech/pylot-gtm/src/GTM'
import { itemHasKeys } from '@corratech/pylot-utils'

interface AdditionalData {
    creative_name: string
    creative_slot: string
    promotion_id: string
    promotion_name: string
}

export type GTMProductData = {
    id: string
    name: string
    currency?: string
    discount?: number
    brand?: string
    category?: string
    price: number
    quantity?: number
    stock_status?: string
    subscription_frequency?: string
}

const EVENT_NAME_MAP = {
    view: 'view_related_products',
    add: 'select_related_product'
}

const crossSellProductsAdditionalData: AdditionalData = {
    creative_name: 'Cross-Sell Products PDP',
    creative_slot: 'cross_sell_products_pdp_1',
    promotion_id: 'csp_1',
    promotion_name: 'Cross-Sell Products'
}

const relatedProductsAdditionalData: AdditionalData = {
    creative_name: 'Related Products PDP',
    creative_slot: 'related_products_pdp_1',
    promotion_id: 'rp_1',
    promotion_name: 'Related Products'
}

const upSellProductsAdditionalData: AdditionalData = {
    creative_name: 'Up-Sell Products PDP',
    creative_slot: 'up_sell_products_pdp_1',
    promotion_id: 'usp_1',
    promotion_name: 'Up-Sell Products'
}

const mandatoryItemProps = ['id', 'name']

const isValidGTMProductItems = (items: unknown): items is GTMProductData[] => {
    if (!Array.isArray(items)) {
        return false
    }
    let result = true
    for (const item of items) {
        if (item && !itemHasKeys(item, mandatoryItemProps)) {
            result = false
        }
    }
    return result
}

export const pushPdpCrossSellProductsView = (
    data: Record<string, unknown>
): GTMEvent => {
    const { crosssell_products_list } = data

    if (!isValidGTMProductItems(crosssell_products_list)) {
        console.info('Invalid GTM Cross-Sell Products Data')
        return { event: '' }
    }

    return {
        event: EVENT_NAME_MAP['view'],
        ...crossSellProductsAdditionalData,
        ecommerce: {
            items: [crosssell_products_list]
        },
        _clear: true
    }
}

export const pushPdpRelatedProductsView = (
    data: Record<string, unknown>
): GTMEvent => {
    const { related_products_list } = data

    if (!isValidGTMProductItems(related_products_list)) {
        console.info('Invalid GTM Related Products Data')
        return { event: '' }
    }

    const related_products_list_available = (
        related_products_list ?? []
    ).filter((product: any) => product.stock_status !== 'OUT_OF_STOCK')

    return {
        event: EVENT_NAME_MAP['view'],
        ...relatedProductsAdditionalData,
        ecommerce: {
            items: [related_products_list_available]
        },
        _clear: true
    }
}

export const pushPdpUpSellProductsView = (
    data: Record<string, unknown>
): GTMEvent => {
    const { upsell_products_list } = data

    if (!isValidGTMProductItems(upsell_products_list)) {
        console.info('Invalid GTM Related Products Data')
        return { event: '' }
    }

    return {
        event: EVENT_NAME_MAP['view'],
        ...upSellProductsAdditionalData,
        ecommerce: {
            items: [upsell_products_list]
        },
        _clear: true
    }
}
export const pushPdpAddToCart = (data: Record<string, unknown>): GTMEvent => {
    const { added_items, eventLocation } = data

    if (!isValidGTMProductItems(added_items)) {
        console.info('Invalid GTM Products Data')
        return { event: '' }
    }
    const additionalDataMap: Record<string, AdditionalData> = {
        RELATED: relatedProductsAdditionalData,
        UPSELL: upSellProductsAdditionalData,
        CROSSSELL: crossSellProductsAdditionalData
    }

    const additionalData =
        additionalDataMap[eventLocation as keyof typeof additionalDataMap] || {}

    return {
        event: EVENT_NAME_MAP['add'],
        ...additionalData,
        ecommerce: {
            items: [added_items]
        },
        _clear: true
    }
}
